import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { useTranslation } from "react-i18next";
import LogoBar from "./LogoBar";
import Home from "./Home";

const Trainer = lazy(() => import("./Trainer"));
const StrategyCalculator = lazy(() => import("./StrategyCalculator"));
const CardCountingTrainer = lazy(() => import("./CardCountingTrainer"));
const StrategyChart = lazy(() => import("./StrategyChart"));
const Glossary = lazy(() => import("./Glossary"));
const About = lazy(() => import("./About"));
const HowToUse = lazy(() => import("./HowToUse"));
const NotFound = lazy(() => import("./NotFound"));

const TOOL_ROUTES = ["/trainer", "/calculator", "/counting"];

function LangFromQuery() {
  const { i18n } = useTranslation();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lng = params.get("lng");
    if ((lng === "he" || lng === "en") && i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [location.search, i18n]);
  return null;
}

function PageFallback() {
  const { t } = useTranslation();
  return (
    <div className="p-10 text-center font-display italic text-gold-foil">
      <div className="text-2xl">{t("loading_strategy")}</div>
      <div className="hairline-divider mx-auto mt-3" style={{ width: 80 }} />
    </div>
  );
}

const Icon = {
  trainer: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="5" width="11" height="15" rx="2" />
      <rect x="9" y="3" width="11" height="15" rx="2" />
      <path d="M14 9h2M14 12h2" strokeLinecap="round" />
    </svg>
  ),
  calculator: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path
        d="M8 7h8M8 11h2M12 11h2M16 11h0M8 15h2M12 15h2M16 15h0M8 19h2M12 19h6"
        strokeLinecap="round"
      />
    </svg>
  ),
  counting: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M3 17l6-6 4 4 8-8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M14 7h7v7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chart: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </svg>
  ),
};

function TabBar() {
  const { t } = useTranslation();
  return (
    <nav className="tabbar" aria-label="Primary">
      <NavLink to="/trainer">
        {Icon.trainer}
        <span>{t("tab_trainer")}</span>
      </NavLink>
      <NavLink to="/calculator">
        {Icon.calculator}
        <span>{t("tab_calc")}</span>
      </NavLink>
      <NavLink to="/counting">
        {Icon.counting}
        <span>{t("tab_count")}</span>
      </NavLink>
      <NavLink to="/strategy-chart">
        {Icon.chart}
        <span>{t("tab_chart")}</span>
      </NavLink>
    </nav>
  );
}

function FooterShelf({ show }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <footer
      className="max-w-3xl mx-auto px-6 pt-6 pb-8 text-center"
      style={{ position: "relative", zIndex: 1 }}
    >
      <div className="deco-divider mb-3">
        <span style={{ fontSize: "0.65rem" }}>Maison HitOrStand</span>
      </div>
      <nav aria-label="Footer">
        <ul
          className="flex flex-wrap justify-center gap-x-5 gap-y-2 font-display italic"
          style={{
            color: "var(--ink-dim)",
            fontSize: "0.82rem",
            letterSpacing: "0.06em",
          }}
        >
          <li>
            <Link to="/strategy-chart">{t("nav_chart")}</Link>
          </li>
          <li>
            <Link to="/glossary">{t("nav_glossary")}</Link>
          </li>
          <li>
            <Link to="/how-to-use">{t("nav_how_to")}</Link>
          </li>
          <li>
            <Link to="/about">{t("nav_about")}</Link>
          </li>
        </ul>
      </nav>
      <p
        className="mt-3 font-display italic"
        style={{
          color: "var(--gold-deep)",
          fontSize: "0.68rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        Practiced, not promised
      </p>
    </footer>
  );
}

function FooterGate() {
  const location = useLocation();
  const onTool = TOOL_ROUTES.some((p) => location.pathname.startsWith(p));
  return <FooterShelf show={!onTool} />;
}

function getInitialTheme() {
  if (typeof window === "undefined") return "dark";
  const saved = localStorage.getItem("theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

export default function App() {
  const { i18n } = useTranslation();
  const isHebrew = i18n.language === "he";
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.dir = isHebrew ? "rtl" : "ltr";
    document.documentElement.lang = isHebrew ? "he" : "en";
  }, [isHebrew]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta)
      meta.setAttribute("content", theme === "light" ? "#f4ecd8" : "#082b1f");
  }, [theme]);

  const toggleLanguage = () =>
    i18n.changeLanguage(isHebrew ? "en" : "he");
  const toggleTheme = () =>
    setTheme((t) => (t === "light" ? "dark" : "light"));

  return (
    <div className="app-felt app-grain">
      <Router>
        <LangFromQuery />

        <LogoBar
          leftSlot={
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "light" ? "Switch to dark" : "Switch to light"}
              className="grid place-items-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: "transparent",
                border: "1px solid var(--rim)",
                color: "var(--rim-light)",
                fontSize: "0.95rem",
              }}
            >
              {theme === "light" ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="4" fill="currentColor" stroke="none"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>
              )}
            </button>
          }
          rightSlot={
            <button
              type="button"
              onClick={toggleLanguage}
              className="grid place-items-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background:
                  "linear-gradient(180deg, #efddae 0%, #c8a44d 60%, #8a6b2a 100%)",
                color: "var(--noir)",
                fontWeight: 800,
                fontFamily: '"Bodoni Moda", serif',
                border: "1px solid var(--noir)",
                boxShadow:
                  "inset 0 0 0 2px var(--noir-soft), 0 4px 12px -4px rgba(200,164,77,0.55)",
                fontSize: "0.82rem",
                letterSpacing: "0.02em",
              }}
              aria-label={isHebrew ? "Switch to English" : "החלף לעברית"}
            >
              {isHebrew ? "EN" : "עב"}
            </button>
          }
        />

        <main
          className="max-w-3xl mx-auto"
          style={{
            minHeight: "calc(100vh - 56px)",
            paddingBottom: "calc(56px + env(safe-area-inset-bottom))",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/trainer" element={<Trainer />} />
              <Route path="/calculator" element={<StrategyCalculator />} />
              <Route path="/counting" element={<CardCountingTrainer />} />
              <Route path="/strategy-chart" element={<StrategyChart />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/about" element={<About />} />
              <Route path="/how-to-use" element={<HowToUse />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <FooterGate />
        </main>

        <TabBar />
      </Router>
      <Analytics />
    </div>
  );
}
